<script setup lang="ts">
import type { DatePickerDate } from "v-calendar/src/use/datePicker";
import type { DatePartsRules } from "v-calendar/src/utils/date/helpers";
import type { PopoverOptions } from "v-calendar/src/utils/popovers";
import type { AttributeConfig } from "v-calendar/src/utils/attribute";

const props = withDefaults(
    defineProps<{
        label?: string | null;
        mode?: "date" | "dateTime" | "time"; // 'date'
        modelValue?: DatePickerDate; // null
        rules?: "auto" | DatePartsRules; // undefined
        isRequired?: boolean; // false
        is24hr?: boolean; // true
        hideTimeHeader?: boolean; // false
        timeAccuracy?: number; // 2
        updateOnInput?: boolean; // true
        inputDebounce?: number; // 1000
        firstDayOfWeek?: number;
        popover?: true | Partial<PopoverOptions>; // true
        dragAttribute?: AttributeConfig; // undefined
        selectAttribute?: AttributeConfig; // undefined
        buttonProps?: Record<string, any>;
    }>(),
    {
        mode: "date",
        modelValue: null,
        rules: undefined,
        label: null,
        isRequired: false,
        is24hr: true,
        hideTimeHeader: false,
        timeAccuracy: 2,
        firstDayOfWeek: 2,
        updateOnInput: true,
        inputDebounce: 1000,
        popover: true,
        dragAttribute: undefined,
        selectAttribute: undefined,
        buttonProps: () => ({}),
    },
);

const emit = defineEmits(["update:model-value", "close"]);

const colorMode = useColorMode();
const isDark = computed(() => colorMode.value === "dark");
const date = computed({
    get: () => props.modelValue,
    set: (value) => {
        emit("update:model-value", value);
        emit("close");
    },
});

const attrs = [
    {
        key: "today",
        highlight: {
            color: "blue",
            fillMode: "outline",
            class: "!bg-gray-100 dark:!bg-gray-800",
        },
        dates: new Date(),
    },
];

const display = computed(() => {
    if (props.label) {
        return props.label;
    } else if (props.modelValue) {
        const date = new Date(props.modelValue as Date);

        if (props.mode === "date") return date.toLocaleDateString();
        else if (props.mode === "time") return date.toLocaleTimeString();
        else return date.toLocaleString();
    }

    return "";
});
</script>
<template>
    <UPopover :popper="{ placement: 'bottom-start' }">
        <UButton
            icon="i-heroicons-calendar-days-20-solid"
            :label="display"
            color="white"
            v-bind="buttonProps"
        />
        <template #panel>
            <VDatePicker
                v-model="date"
                transparent
                borderless
                :attributes="attrs"
                :is-dark="isDark"
                title-position="left"
                trim-weeks
                :mode="mode"
                :first-day-of-week="firstDayOfWeek"
                :rules="rules"
                :is-required="isRequired"
                :is24hr="is24hr"
                :hide-time-header="hideTimeHeader"
                :time-accuracy="timeAccuracy"
                :update-on-input="updateOnInput"
                :input-debounce="inputDebounce"
                :popover="popover"
                :drag-attribute="dragAttribute"
                :select-attribute="selectAttribute"
            />
        </template>
    </UPopover>
</template>
